import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DrawerTab } from "@/components/atoms/DrawerTabs";
import DrawerTabs from "@/components/atoms/DrawerTabs";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";

import { GroupOrder, GroupOrderClientSide, Order, Task } from "@/types";
import { useGetCompanyAddresses } from "@/services/Company";
import { postGroupOrders, useGetOrdersByIds } from "@/services/Orders";
import { findDeliveryAddress } from "@/utils/AddressHelper";

import SellerTab from "./TabPanels/SellerTab";
import OrdersTab from "./TabPanels/OrdersTab";
import TransportTab from "./TabPanels/TransportTab";

import styles from "./styles.module.css";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";

interface Props {
  task: Task;
  order: Order | undefined;
  currentCompanyId: number | undefined;
  onClose: () => void;
}

const CollectiveInvoicingTask: React.FC<Props> = (props) => {
  const { task, order, currentCompanyId, onClose } = props;

  const [shippingAddress, setShippingAddress] = useState<string | undefined>(undefined);
  const [counterpartyId, setCounterpartyId] = useState<number | undefined>(undefined);
  const [groupOrder, setGroupOrder] = useState<GroupOrderClientSide[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { orders: initialOrders, isLoading: isLoadingOrders } = useGetOrdersByIds(task.orderIds);

  const { mutateAsync: handleGrouping, isPending } = useMutation({
    mutationFn: async () => {
      const translatedGroup: GroupOrder = {
        taskId: task.id,
        groups: [],
      };

      groupOrder.forEach((clientSideGroup) => {
        const orderIds = clientSideGroup.groups.map((group) => group.orderId);
        translatedGroup.groups.push({ orderIds });
      });

      return await postGroupOrders(translatedGroup);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getOpenTasks", currentCompanyId],
      });
      onClose();
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.deleteError"), t("notifications:tasks.groupOrdersError"))
      );
    },
  });

  useEffect(() => {
    if (isLoadingOrders) return;
    setOrders(initialOrders);
  }, [isLoadingOrders]);

  useEffect(() => {
    if (!order || !currentCompanyId) return;
    const foundCounterpartyId = order?.companiesToTradeWith?.find((companyId) => companyId !== currentCompanyId);
    setCounterpartyId(foundCounterpartyId);
  }, [order, currentCompanyId]);

  const { data: counterCompany, isLoading: isLoadingCompanyAddresses } = useGetCompanyAddresses(
    counterpartyId as number,
    {
      enabled: counterpartyId !== undefined,
    }
  );

  useEffect(() => {
    if (!counterCompany || isLoadingCompanyAddresses) return;
    const filteredResult = findDeliveryAddress(counterCompany);
    setShippingAddress(filteredResult);
  }, [counterCompany, isLoadingCompanyAddresses]);

  const isApproveDisabled = orders.length > 0 || isPending;

  return (
    <DrawerTabs defaultValue="seller" className="full flex col between h-100 py-10">
      <Group gap="20px" align="center" className={styles.taskHeader}>
        <SVGAsset name="cargo-truck" />
        <Text size="md" display="technical" tt="uppercase" strong={true}>
          {t("tasks:groupTransport")}
        </Text>
      </Group>

      <DrawerTabs.List>
        <DrawerTabs.Tab value="seller">
          <DrawerTab svgName="office" label={t("tasks:seller")} />
        </DrawerTabs.Tab>
        <DrawerTabs.Tab value="orders">
          <DrawerTab svgName="hamburger" label={t("tasks:orders")} />
        </DrawerTabs.Tab>
        <DrawerTabs.Tab value="loads" disabled={groupOrder.length === 0}>
          <DrawerTab svgName="cargo-truck" label={t("tasks:loads")} />
        </DrawerTabs.Tab>
      </DrawerTabs.List>
      <DrawerTabs.Panel value="seller" className={styles.tabPanel}>
        <SellerTab shippingAddress={shippingAddress} task={task} onClose={onClose} />
      </DrawerTabs.Panel>
      <DrawerTabs.Panel value="orders" className={styles.tabPanel}>
        <OrdersTab
          isLoading={isLoadingOrders}
          onClose={onClose}
          orderIds={task.orderIds}
          taskId={task.id}
          setGroupOrder={setGroupOrder}
          groupOrder={groupOrder}
          orders={orders}
          setOrders={setOrders}
        />
      </DrawerTabs.Panel>

      <DrawerTabs.Panel value="loads" className={styles.tabPanel}>
        <TransportTab
          onClose={onClose}
          groupOrders={groupOrder}
          setGroupOrders={setGroupOrder}
          setOrders={setOrders}
          initialOrders={initialOrders}
        />
      </DrawerTabs.Panel>

      <Group className={styles.tabActionGroup} p={15} gap={16} align="center" justify="flex-end">
        <CTA
          data-testid="collective-invoicing-approve-btn"
          intent="primary"
          size="sm"
          fullWidth={false}
          type="button"
          onClick={() => handleGrouping()}
          disabled={isApproveDisabled}
        >
          {t("actions:approve")}
        </CTA>
        <CTA intent="secondary" size="sm" fullWidth={false} type="button" onClick={onClose}>
          {t("actions:shutdown")}
        </CTA>
      </Group>
    </DrawerTabs>
  );
};

export default CollectiveInvoicingTask;
